import { count } from '../core/words.js';
import { ft } from '../core/units.js';
import { describe as describeWriteup } from './writeups.js';
import { gateFor } from '../data/staff.js';
import { TALK_REACH } from './conversation.js';

/**
 * Your manager crossing the property to tell you where you stand, and what
 * he says when he gets there. Armed by `escalation.js`, delivered here, and
 * said through the conversation like anybody else's words.
 */
export function createManagerWarning({ npcs, player, escalation, writeups, clock, hud, autosave, conversation, modalOpen }) {
  /** The one person on the roster who comes and finds you, and his rule. */
  function theManager() {
    const person = gateFor('management');
    if (!person?.warns) return null;
    const npc = npcs.find((candidate) => candidate.data.id === person.id);
    return npc ? { npc, rule: person.warns } : null;
  }

  /**
   * How long he walks at where you were before looking again.
   *
   * There is no pathfinding in this game and there is not going to be — the
   * routes are authored walkable and a steering NPC is an unlearnable one. So an
   * interception is a series of short walks at you rather than a path to you: he
   * heads for where you are, and a few seconds later he heads for where you are
   * now. Down a corridor that is a man following you. Against a wall it is a man
   * giving up, going back to his route, and trying again from somewhere else,
   * which is both cheap and roughly what a person does.
   */
  const INTERCEPT_RETARGET = 3.5;
  let interceptIn = 0;

  /**
   * Your manager crossing the property to tell you where you stand.
   *
   * Armed by the second write-up and delivered by him arriving, which is the
   * whole mechanic: everything else in this game that tells you something is
   * something you went and read. He is the one thing that comes to you.
   *
   * On the clock only. Off it he is a manager who has gone home, and a warning
   * that ambushes you in the corridor on your day off is a cutscene.
   */
  function interceptWarning(dt, status) {
    if (!escalation.warningDue || !status.onTheClock || modalOpen()) return;
    const him = theManager();
    if (!him || !him.npc.present) return;

    const dx = him.npc.position.x - player.position.x;
    const dz = him.npc.position.z - player.position.z;
    const distance = Math.hypot(dx, dz);

    // Out of reach is not a failure state. He has not found you yet, and he will
    // keep walking his route until he does.
    if (distance > ft(him.rule.within)) {
      interceptIn = 0;
      return;
    }
    if (distance <= TALK_REACH) {
      deliverWarning(him);
      return;
    }

    interceptIn -= dt;
    if (interceptIn > 0) return;
    interceptIn = INTERCEPT_RETARGET;
    him.npc.goAndLook(player.position);
  }

  /**
   * What he actually says, which is the three things a warning has to contain.
   *
   * What it was for, what the next one costs, and when the ones you have got come
   * off — with the dates, because a warning without a date is a threat. The lines
   * around them are placeholders and the numbers between them are not.
   */
  function warningFrom(rule) {
    const on = writeups.entries;
    const next = writeups.nextSuspension();
    const clears = on.length ? Math.max(...on.map((entry) => entry.clears)) : clock.day;
    return {
      kicker: `${on.length} of ${writeups.max}`,
      lines: rule.lines,
      facts: [
        ...on.map((entry) => `${describeWriteup(entry.reason)}, day ${entry.day}, clears day ${entry.clears}`),
        next.dismissed
          ? 'A third one and you are finished here. There is no fourth conversation.'
          : `A third one is ${count(next.shifts, 'shift')} off the roster.`,
        `Work clean and your card is empty on day ${clears}.`,
      ],
    };
  }

  function deliverWarning(him) {
    escalation.warningHeard();
    interceptIn = 0;
    him.npc.clearSuspicion();
    conversation.tell(him.npc, warningFrom(him.rule));
    hud.say(`${him.npc.data.name} has come to find you.`, { tone: 'bad', seconds: 5 });
    autosave.touch();
  }

  return { interceptWarning };
}
